import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { usePageFrom, useTrack } from '../../lib/tracking-context';
import { DesktopPromo } from './DesktopPromo';
import { DownloadButton } from './DownloadButton';
import { SetupSteps } from './SetupSteps';
import {
  desktopPlatforms,
  detectSystem,
  platformIds,
  type DesktopPlatform,
  type PlatformId,
} from './systems';
import './download-app.css';

interface AvailableDownload {
  readonly platform: PlatformId;
  readonly version: string;
  /** In bytes; servers before sizes were listed leave it out. */
  readonly size?: number;
}

type Listing =
  | { readonly state: 'loading' }
  | { readonly state: 'failed' }
  | { readonly state: 'ready'; readonly downloads: readonly AvailableDownload[] };

const apiBase = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

// How long a download button says the download has started.
const STARTED_MS = 4000;

function parseDownloads(body: unknown): AvailableDownload[] {
  if (!body || typeof body !== 'object' || !('downloads' in body)) {
    throw new Error('Invalid downloads response');
  }
  const listed: unknown = body.downloads;
  if (!Array.isArray(listed)) throw new Error('Invalid downloads response');
  const available: AvailableDownload[] = [];
  for (const item of listed as unknown[]) {
    if (!item || typeof item !== 'object' || !('platform' in item) || !('version' in item))
      continue;
    if (
      typeof item.version === 'string' &&
      platformIds.some((platform) => platform === item.platform)
    ) {
      const size = 'size' in item && typeof item.size === 'number' ? item.size : 0;
      available.push({
        platform: item.platform as PlatformId,
        version: item.version,
        ...(size > 0 && { size }),
      });
    }
  }
  return available;
}

export function DownloadApp({ headingLevel = 2 }: { readonly headingLevel?: 1 | 2 }) {
  const { t } = useI18n();
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const Subheading = headingLevel === 1 ? 'h2' : 'h3';
  const [listing, setListing] = useState<Listing>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  // Each download shows how to set it up once it starts; the button says so for a moment.
  const [started, setStarted] = useState<ReadonlySet<PlatformId>>(new Set());
  const [justStarted, setJustStarted] = useState<PlatformId | null>(null);
  const [system] = useState(() => detectSystem());
  const track = useTrack();
  const from = usePageFrom();
  // This computer's system first, then the others in their usual order.
  const platforms = [...desktopPlatforms].sort(
    (a, b) => Number(b.system === system) - Number(a.system === system),
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetch(`${apiBase}/api/v1/downloads`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Could not load downloads');
        setListing({ state: 'ready', downloads: parseDownloads(await response.json()) });
      })
      .catch(() => {
        if (!controller.signal.aborted) setListing({ state: 'failed' });
      });
    return () => {
      controller.abort();
    };
  }, [attempt]);

  useEffect(() => {
    if (!justStarted) return;
    const timer = setTimeout(() => {
      setJustStarted(null);
    }, STARTED_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [justStarted]);

  function start(id: PlatformId) {
    track({ event: 'installer', platform: id, from });
    setStarted((current) => new Set(current).add(id));
    setJustStarted(id);
  }

  return (
    <section className="download-app" id="download-app" aria-labelledby="download-app-heading">
      <div className="download-app-intro">
        <Heading id="download-app-heading">{t.downloads.heading}</Heading>
        <p>{t.downloads.description}</p>
      </div>
      <div className="download-app-platforms">
        {platforms.map((platform) => (
          <article
            className="download-app-platform"
            key={platform.id}
            aria-labelledby={`download-${platform.id}`}
          >
            <div className="download-app-platform-head">
              <span className="download-app-glyph" aria-hidden="true">
                <Icon name={platform.icon} />
              </span>
              <Subheading className="download-app-platform-name" id={`download-${platform.id}`}>
                {platform.name}
              </Subheading>
            </div>
            <Details platform={platform} listing={listing} />
            <DownloadAction
              platform={platform}
              listing={listing}
              started={justStarted === platform.id}
              onStart={() => {
                start(platform.id);
              }}
            />
            {started.has(platform.id) && <SetupSteps platform={platform} />}
          </article>
        ))}
      </div>
      {listing.state === 'failed' && (
        <div className="download-app-failed" role="alert">
          <Icon name="alert" />
          <span>{t.downloads.failed}</span>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              setListing({ state: 'loading' });
              setAttempt((count) => count + 1);
            }}
          >
            <Icon name="retry" />
            {t.common.retry}
          </button>
        </div>
      )}
      <p className="sr-only" role="status">
        {listing.state === 'loading' ? t.downloads.loading : justStarted ? t.downloads.started : ''}
      </p>
      {headingLevel === 1 && <DesktopPromo />}
    </section>
  );
}

function Details({
  platform,
  listing,
}: {
  readonly platform: DesktopPlatform;
  readonly listing: Listing;
}) {
  const { format } = useI18n();
  const download =
    listing.state === 'ready'
      ? listing.downloads.find((item) => item.platform === platform.id)
      : undefined;
  return (
    <ul className="download-app-details">
      <li>{platform.architecture}</li>
      {download && <li>v{download.version}</li>}
      <li>{platform.format}</li>
      {download?.size !== undefined && <li>{format.fileSize(download.size)}</li>}
      {listing.state === 'loading' && (
        <li className="download-app-details-pending" aria-hidden="true" />
      )}
    </ul>
  );
}

function DownloadAction({
  platform,
  listing,
  started,
  onStart,
}: {
  readonly platform: DesktopPlatform;
  readonly listing: Listing;
  readonly started: boolean;
  readonly onStart: () => void;
}) {
  const { t } = useI18n();
  if (listing.state !== 'ready') {
    return (
      <span
        className={cx(
          'download-app-pending',
          listing.state === 'failed' && 'download-app-pending--idle',
        )}
        aria-hidden="true"
      />
    );
  }
  const download = listing.downloads.find((item) => item.platform === platform.id);
  if (!download) return <span className="download-app-unavailable">{t.downloads.unavailable}</span>;
  return (
    <DownloadButton
      href={`${apiBase}/api/v1/downloads/${platform.id}`}
      label={`${t.downloads.download} ${platform.name} ${platform.architecture}`}
      started={started}
      onStart={onStart}
    />
  );
}
